/**
 * Turn a typed query object into a query string.
 *
 * Derived from the object's own keys rather than a hand-written list of the
 * ones to include. That is the whole point: the hand-written version is a
 * mirror of a Zod schema that nothing keeps in step, and when `treeId` was
 * added to the document filters and not to the mirror, every request for one
 * tree's photos went out with no scope at all — so the API returned every
 * photo in the system and each tree appeared to show all of them.
 *
 * A filter silently dropped does not fail. It widens the query, which is the
 * worst way for this to go wrong: more data comes back, nothing errors, and it
 * looks like a UI bug rather than a missing parameter.
 *
 * Omitted rather than sent: `undefined`, `null`, `false`, and the empty string.
 * A false flag means "did not ask for it" everywhere in this API, and sending
 * `library=false` would read as a filter rather than its absence.
 */
export function toQueryString(
  query: Record<string, string | number | boolean | null | undefined>,
): string {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === false) continue
    if (value === '') continue
    params.set(key, value === true ? 'true' : String(value))
  }

  const encoded = params.toString()
  return encoded === '' ? '' : `?${encoded}`
}
